import { useQuery } from "@tanstack/react-query";
import { Alert, Form, Select, Typography, message } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { useClashWarning } from "components/modals/ClashWarningModal";
import { checkScheduleClashesV2 } from "hooks/api/schedule-v2";
import { colors } from "shared/theme/color";
import { useUpdateScheduleV2 } from "hooks/mutations/schedule-v2";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import { TimeSection } from "pages/schedule/drawer/TimeSection";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import { translate, translateWithVars } from "shared/languages";
import { Schedule } from "shared/types";
import { UserType } from "shared/types/enum";
import {
  combineDateTime,
  DEFAULT_TIMEZONE,
  tzDate,
} from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

export interface UpdateScheduleFormBodyHandle {
  submit: () => void;
  reset: () => void;
}

interface UpdateScheduleFormBodyProps {
  schedule: Schedule;
  // Pre-filled date+time. Defaults to schedule.start_time when omitted.
  initialDateTime?: string | Date;
  // Gates queries and field re-hydration; pass the parent modal's `open` state.
  enabled: boolean;
  onUpdated?: (nextScheduleId: string, nextTimestamp: string) => void;
  // True while the picked instructor and time can't be saved: the studio's
  // Cegah Jadwal Bentrok is on and the instructor already teaches then (or
  // that check is still running). The parent disables its confirm button.
  onBlockedChange?: (blocked: boolean) => void;
}

export const UpdateScheduleFormBody = forwardRef<
  UpdateScheduleFormBodyHandle,
  UpdateScheduleFormBodyProps
>(({ schedule, initialDateTime, enabled, onUpdated, onBlockedChange }, ref) => {
  const seedDateTime = initialDateTime ?? schedule.start_time;

  // Moving an occurrence preserves its OWN length — the server re-derives
  // end_time from the schedule's stored duration, not the live class duration.
  // Mirror that in the end-time preview so the form doesn't mislead when the
  // class's duration was changed after this schedule was created. Falls back to
  // the class duration if the stored times are missing/invalid.
  const storedDurationMin = tzDate(schedule.end_time).diff(
    tzDate(schedule.start_time),
    "minute",
  );
  const previewClass = {
    ...schedule.class_type,
    duration:
      Number.isFinite(storedDurationMin) && storedDurationMin > 0
        ? storedDurationMin
        : schedule.class_type.duration,
  };

  const [form] = useForm();
  const scheduleDate = useWatch("schedule_date", form);
  const startTime = useWatch("start_time", form);
  const instructorId = useWatch("instructor_id", form);

  const { data: businessSettings } = useBusinessSettings();
  // Employee management hard filter (mirrors CreateScheduleDrawer): only
  // instructors assigned to this schedule's class type are selectable, when
  // both the master and the class-assignment sub-flag are on.
  const { data: staffs, isLoading: staffLoading } = getAllStaffByBusinessId(
    UserType.Instructor,
    {
      is_active: true,
      ...(businessSettings?.employee_management_enabled &&
      businessSettings?.employee_management_class_assignment_enabled
        ? { class_type_id: schedule.class_type.id }
        : {}),
    },
    { enabled },
  );
  const { mutate: updateSchedule } = useUpdateScheduleV2();
  const { checkAndConfirm, clashWarning } = useClashWarning();

  // Cegah Jadwal Bentrok: the server refuses a move that double-books the
  // instructor, so check the picked instructor and time as they change and
  // block Perbarui up front, instead of letting the admin confirm a clash
  // warning only to be refused. Client clashes stay a warning on submit.
  const preventConflicts =
    !!businessSettings?.schedule_conflict_prevention_enabled;
  const proposedIso = useMemo(() => {
    if (!scheduleDate || !startTime) return null;
    const combined = combineDateTime(
      scheduleDate,
      startTime,
      schedule.timezone ?? DEFAULT_TIMEZONE,
    );
    return combined ? combined.utc().toISOString() : null;
  }, [scheduleDate, startTime, schedule.timezone]);
  const clashCheck = useQuery({
    queryKey: [
      "schedule-v2",
      "reschedule-clash",
      schedule.schedule_id,
      schedule.occurrence_timestamp,
      instructorId,
      proposedIso,
    ],
    queryFn: () =>
      checkScheduleClashesV2({
        class_id: schedule.class_type.id,
        instructor_id: instructorId,
        start_time: proposedIso as string,
        schedule_id: schedule.schedule_id,
        occurrence_timestamp: schedule.occurrence_timestamp,
      }),
    enabled: enabled && preventConflicts && !!instructorId && !!proposedIso,
    // A failed check must not hold Perbarui disabled through react-query's
    // retries; the save is checked by the server anyway.
    retry: false,
  });
  const instructorClashes = preventConflicts
    ? clashCheck.data?.instructor_clashes ?? []
    : [];
  // A failed check doesn't block: the save is still checked by the server.
  const blocked =
    preventConflicts &&
    !!instructorId &&
    !!proposedIso &&
    ((clashCheck.isPending && clashCheck.fetchStatus !== "idle") ||
      instructorClashes.length > 0);
  useEffect(() => {
    onBlockedChange?.(blocked);
  }, [blocked, onBlockedChange]);
  const instructorName =
    staffs?.find((staff) => staff.id === instructorId)?.full_name ??
    (instructorId === schedule.instructor_id
      ? schedule.instructor_name
      : undefined);

  useEffect(() => {
    if (!enabled) return;
    form.setFieldsValue({
      instructor_id: schedule.instructor_id,
      schedule_date: tzDate(seedDateTime),
      start_time: tzDate(seedDateTime),
    });
    // seedDateTime is derived from props; re-hydrate when it (or open state) changes.
  }, [form, enabled, schedule.instructor_id, seedDateTime]);

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        if (blocked) return;
        form
          .validateFields()
          .then((values) => {
            const newOccurrenceTimestamp = combineDateTime(
              values.schedule_date,
              values.start_time,
              schedule.timezone ?? DEFAULT_TIMEZONE,
            );
            if (!newOccurrenceTimestamp) {
              message.error(translate("messages.error.invalid_form"));
              return;
            }
            const nextOccurrenceTimestamp = newOccurrenceTimestamp
              .utc()
              .toISOString();

            // Same allow-with-warning gate as create. Exclude this schedule so
            // it isn't reported as clashing with itself; the server derives the
            // affected clients from the excluded schedule's bookings.
            checkAndConfirm(
              {
                class_id: schedule.class_type.id,
                instructor_id: values.instructor_id,
                start_time: nextOccurrenceTimestamp,
                schedule_id: schedule.schedule_id,
                occurrence_timestamp: schedule.occurrence_timestamp,
              },
              () =>
                updateSchedule(
                  {
                    schedule_id: schedule.schedule_id,
                    occurrence_timestamp: schedule.occurrence_timestamp,
                    new_occurrence_timestamp: nextOccurrenceTimestamp,
                    new_instructor_id: values.instructor_id,
                  },
                  {
                    onSuccess: (response) => {
                      const updated = response.data;
                      const nextScheduleId =
                        updated?.id ?? schedule.schedule_id;
                      const nextTimestamp =
                        updated?.start_time ?? nextOccurrenceTimestamp;
                      onUpdated?.(nextScheduleId, nextTimestamp);
                    },
                  },
                ),
            );
          })
          .catch(() => {
            message.error(translate("messages.error.invalid_form"));
          });
      },
      reset: () => form.resetFields(),
    }),
    [form, schedule, updateSchedule, onUpdated, checkAndConfirm, blocked],
  );

  return (
    <>
      {clashWarning}
      {schedule.is_recurring && (
        // Quiet token note instead of a boxed Alert — the popup prototype keeps
        // secondary information as muted text.
        <Typography.Text
          style={{
            display: "block",
            fontSize: textPx(12.5),
            color: colors.ink3,
            margin: "8px 0 6px",
          }}
        >
          {translate("messages.info.update_single_occurrence")}
        </Typography.Text>
      )}
      <Form
        form={form}
        layout="vertical"
        name="updateScheduleForm"
        style={{ marginTop: 10 }}
        initialValues={{
          instructor_id: schedule.instructor_id,
          schedule_date: tzDate(seedDateTime),
          start_time: tzDate(seedDateTime),
        }}
      >
        <Form.Item
          name="instructor_id"
          label={translate("label.instructor_name")}
          rules={[
            {
              required: true,
              message: `${translate("form.error.select")} ${translate(
                "label.instructor_name",
              )}`,
            },
          ]}
        >
          <Select
            placeholder={`${translate(
              "form.placeholders.select",
            )} ${translate("label.instructor_name")}`}
            loading={staffLoading}
            onChange={() => form.setFieldValue("start_time", undefined)}
            showSearch
            filterOption={personFilterOption}
            optionRender={renderPersonOption}
            options={[
              // Current instructor pinned first. Their phone comes from the
              // staff list when present (absent if they were deactivated —
              // the option then just has no second line).
              {
                value: schedule.instructor_id,
                label: schedule.instructor_name,
                phone: staffs?.find((s) => s.id === schedule.instructor_id)
                  ?.phone_number,
              },
              ...(staffs
                ?.filter((staff) => staff.id !== schedule.instructor_id)
                .map((staff) => ({
                  value: staff.id,
                  label: staff.full_name,
                  phone: staff.phone_number,
                })) ?? []),
            ]}
          />
        </Form.Item>
        <TimeSection
          form={form}
          selectedClass={previewClass}
          scheduleDate={scheduleDate}
          startTime={startTime}
          timeEnabled
          maxOccurrences={undefined}
          maxScheduleDate={undefined}
          showRecurrence={false}
        />
        {instructorClashes.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 4 }}
            message={translateWithVars("clash.instructor_blocked", {
              name: instructorName ?? translate("instructor"),
            })}
          />
        )}
      </Form>
    </>
  );
});
